(ns rothko.form2.component
  (:require [medley.core :refer [map-vals]]
            [reagent.core :as r]
            [rothko.form2.core :as core]
            [rothko.form2.validation :as validation]
            [taoensso.timbre :as log]))

;; rothko#229 disabled? should be an appearance and ns'd key

(defn update-field!
  [!state
   {:keys [::on-field-change
           ::on-form-change]
    :or {on-field-change (constantly :no-op)
         on-form-change (constantly :no-op)}
    :as form-props}
   {:keys [::coercer ::name] :as field}
   value]
  (let [value (if coercer (coercer value) value)
        state (swap! !state assoc-in [::fields name ::current] value)
        current (core/form-current state)]

    (swap! !state assoc ::current current)

    (when (validation/validate-field! !state field)
      (swap! !state assoc-in [::fields name ::value] value))

    (when (validation/validate-form! !state form-props)
      (swap! !state assoc ::value current))

    (on-field-change (get-in @!state [::fields name]))
    (on-form-change @!state)))

(defn get-initial-value
  [{form-initial-values ::initial-value :as form-settings}
   {:keys [::name] field-initial-value ::initial-value :as field-config}]
  (let [form-initial-value (get form-initial-values name)]
    (when (and form-initial-value field-initial-value)
      (log/warn (str "Found initial value in both form-settings and field-settings; "
                     "form-settings value is being overridden by the value set in field-settings")))
    (or field-initial-value form-initial-value)))

(defn make-field-props
  [!state
   form-settings
   {:keys [::name] :as field-config}]
  (let [set-value-cleanly! (partial update-field! !state form-settings field-config)
        set-dirty! #(swap! !state (fn [state]
                                    (-> state
                                        (assoc-in [::fields name ::dirty?] true)
                                        (assoc ::dirty? true))))]
    (assoc field-config
           ::initial-value (get-initial-value form-settings field-config)
           ::set-dirty! set-dirty!
           ::set-clean! #(swap! !state assoc-in [::fields name ::dirty?] false)
           ::set-value-cleanly! set-value-cleanly!
           ::set-value! (fn set-value!
                          [value]
                          (set-value-cleanly! value)
                          (set-dirty!))
           ::get-value #(get-in @!state [::fields name ::value]))))

(defn make-submit!-handler
  [!state {:keys [::on-submit ::on-submit*] :as form-props} field-props]
  (fn submit!
    []
    (let [{:keys [::fields ::validation/valid?]} @!state]
      (if valid?
        (let [values (map-vals ::current fields)]
          (cond on-submit (on-submit values)
                on-submit* (do (swap! !state assoc ::submitting? true)
                               (on-submit* values
                                           #(swap! !state assoc ::submitting? false)))))
        (core/mark-all-fields-dirty! !state field-props)))))

(defn add-constructor-form-props
  [form-settings !state field-settings]
  (assoc form-settings
         ::get-valid? #(::get-valid? @!state)
         ::get-value #(::value @!state)
         ::get-current #(::current @!state)
         ::get-submitting? #(::submitting? @!state)
         ::submit! (make-submit!-handler !state form-settings field-settings)))

(defn compute-combined-disabled?
  [{form-disabled? ::disabled? :as form-settings}
   {field-disabled? ::disabled? :as field-config}]
  (or (core/compute-disabled? form-disabled? form-settings field-config)
      (core/compute-disabled? field-disabled? form-settings field-config)))

(defn field-props->render-props
  [!state form-settings {:keys [::name] :as field-config}]
  (-> field-config
      (assoc ::dirty? (get-in @!state [::fields name ::dirty?] false)
             ::disabled? (compute-combined-disabled? form-settings field-config)
             ::value (get-in @!state [::fields name ::current]))
      (validation/field-props->render-props !state form-settings)))

(defn set-initial-form-value!
  [constructor-field-props]
  ;; Setting initial form values. We do this via the `::set-value-cleanly!` fn on the field so
  ;; that the initial values are validated. We need to do this as we could load a form with
  ;; initial values and then submit it without changing the value but we still need to know
  ;; whether it's valid or not.
  (doseq [[_ {:keys [::set-value-cleanly! ::initial-value]}] constructor-field-props]
    (set-value-cleanly! initial-value)))

(defn init-field-states
  [field-settings]
  (assoc field-settings
         ::current nil
         ::dirty? false
         ::value nil))

(defn init-form-state
  [field-settings]
  {::current nil
   ::dirty? false
   ::fields (map-vals init-field-states field-settings)
   ::value nil})

(defn form*
  [form-settings field-settings form-component]
  ;; TODO rothko#201 assert that all args have the correct shape
  (assert (and (nil? (::component-did-mount form-settings))
               (nil? (::component-will-unmount form-settings))))
  (assert (= (set (keys field-settings))
             (into #{} (comp (map second)
                             (map ::name))
                   field-settings)))

  (let [!state
        (-> (init-form-state field-settings)
            (validation/init-form-state)
            (r/atom))

        constructor-form-props
        (-> form-settings
            (add-constructor-form-props !state field-settings)
            (validation/add-constructor-form-props !state field-settings))

        constructor-field-props
        (map-vals (partial make-field-props !state form-settings)
                  field-settings)]

    (set-initial-form-value! constructor-field-props)

    (r/create-class
     ;; rothko#204 extract into HOC
     {:component-did-mount
      (fn []
        (when-let [lifecycle-hook (::mount form-settings)]
          (lifecycle-hook constructor-form-props constructor-field-props)))

      ;; rothko#204 extract into HOC
      :component-will-unmount
      (fn []
        (when-let [lifecycle-hook (::unmount form-settings)]
          (lifecycle-hook constructor-form-props constructor-field-props)))

      :reagent-render
      ;; It is important that, except for `::render-props`, we do not use the `:reagent-render`
      ;; arguments. The reason is that in the 'constructor' above we decorate these props with the
      ;; form functionality and as such they will not be available if we use the shadowed versions
      ;; passed to the `:reagent-render` function. It is also for this reason that we grab
      ;; `::render-props` from the `:reagent-render` props and make sure the old value (from
      ;; component construction time) is dissoc'd when we create the `render-form-props` (to be
      ;; explicit as technically it will be over written in the `merge`) as we do want to re-render
      ;; the form if this changes outside the form.
      ;; Update to the above comment: now we are also grabbing `::validation/validate-now?`
      ;; option from `:reagent-render` args for on-demand validation because this option is dynamic.
      (fn [{:keys [::render-props ::validation/validate-now?]} _ _]
        (let [render-form-props
              (merge
               ;; TODO need to modify these
               (select-keys @!state [::validation/valid? ::value])
               (dissoc constructor-form-props
                       ::mount
                       ::unmount
                       ::render-props
                       ::validation/validate-now?)
               render-props)

              render-field-props
              (map-vals (partial field-props->render-props !state render-form-props)
                        constructor-field-props)]
          (when validate-now?
            (validation/call-validate! constructor-form-props))
          [form-component render-form-props render-field-props]))})))

(defn set-name-attribute
  [[field-name field-config]]
  [field-name (update field-config ::name #(or % field-name))])

(defn form
  ([field-settings form-component]
   (form nil field-settings form-component))
  ([form-settings field-settings form-component]
   (let [field-settings (into {} (map set-name-attribute) field-settings)]
     ^{:key (::initial-value form-settings)}
     [form* form-settings field-settings form-component])))
